import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { fmtDate, fmtDateFull, todayISO, bakuISO } from '../../utils/dateFormat';
import { confirm } from '../../components/ConfirmDialog';
import { useFoodDeliveryInfo } from '../../utils/foodDelivery';

// ── helpers ───────────────────────────────────────────────────────────────────

const n = (v) => Number(v || 0);

const fmt2 = (v) => {
    const x = n(v);
    return x.toFixed(2);
};

const fmtAZ = (v) => {
    const x = n(v);
    return `${x.toFixed(2)} ₼`;
};

// returns YYYY-MM-DD for N days ago, in Baku timezone
const daysAgo = (d) => {
    const dt = new Date();
    dt.setDate(dt.getDate() - d);
    return bakuISO(dt);
};

// ── Left panel row component ──────────────────────────────────────────────────

function Row({ label, value, color = 'default', bold = false, large = false, sub = false }) {
    const colorMap = {
        default: 'text-slate-700',
        green:   'text-emerald-600',
        red:     'text-red-500',
        blue:    'text-blue-600',
        muted:   'text-slate-400',
    };
    const textCls = colorMap[color] || 'text-slate-700';
    return (
        <div className={`flex items-center justify-between ${sub ? 'pl-4' : ''} ${large ? 'py-2' : 'py-1.5'}`}>
            <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} text-slate-600 ${sub ? 'text-xs text-slate-500' : ''}`}>
                {label}
            </span>
            <span className={`font-semibold tabular-nums ${large ? 'text-lg' : 'text-sm'} ${textCls} ${bold ? 'font-bold' : ''}`}>
                {fmtAZ(value)}
            </span>
        </div>
    );
}

function Divider() {
    return <div className="my-1 border-t border-slate-100" />;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function KassaLedger() {
    const { user } = useAuthStore();
    const isAdmin  = user?.role === 'admin' || user?.role === 'superadmin';

    // Date range (default: last 7 days → today)
    const [fromDate, setFromDate] = useState(daysAgo(6));
    const [toDate,   setToDate]   = useState(todayISO());

    // Data
    const [days,    setDays]    = useState([]);  // array of day objects
    // Food delivery sütunu: funksiya aktivdirsə və ya aralıqda delivery satışı varsa (söndürüləndən sonra da görünsün)
    const foodDeliveryOn = useFoodDeliveryInfo().tenant;
    const showDelivery = foodDeliveryOn || days.some((d) => n(d.restoran_delivery) > 0);
    const [refunds, setRefunds] = useState([]);  // geri qaytarılma düzəlişləri
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);  // kassa hesabatın başlanğıc tarixi

    // Selected day for left panel
    const [selectedDate, setSelectedDate] = useState(todayISO());

    // Edit modal for opening / ofis
    const [editModal, setEditModal]   = useState(false);
    const [editDate,  setEditDate]    = useState('');
    const [editOpen,  setEditOpen]    = useState('');
    const [editOfis,  setEditOfis]    = useState('');
    const [editNotes, setEditNotes]   = useState('');
    const [saving,    setSaving]      = useState(false);

    // ── Load ─────────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/kassa-ledger/daily', { params: { from: fromDate, to: toDate } });
            const data = res.data.data || [];
            setDays(data);
            setRefunds(res.data.refunds || []);
            if (res.data.start_date) setStartDate(res.data.start_date);

            // Auto-select today if in range, else last day
            const today = todayISO();
            const todayRow = data.find(d => d.date === today);
            if (todayRow) setSelectedDate(today);
            else if (data.length) setSelectedDate(data[data.length - 1].date);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    // ── Selected day data ─────────────────────────────────────────────────────
    const selected = days.find(d => d.date === selectedDate) || null;

    // ── Edit helpers ──────────────────────────────────────────────────────────
    const openEdit = (day) => {
        setEditDate(day.date);
        setEditOpen(fmt2(day.opening_balance));
        setEditOfis(fmt2(day.ofis_amount));
        setEditNotes(day.notes || '');
        setEditModal(true);
    };

    const saveEdit = async () => {
        if (editOpen === '' || isNaN(Number(editOpen))) return toast.error('Əvvələ qalığı daxil edin.');
        setSaving(true);
        try {
            await api.post('/kassa-ledger/set-day', {
                date:            editDate,
                opening_balance: Number(editOpen),
                ofis_amount:     Number(editOfis) || 0,
                notes:           editNotes || null,
            });
            toast.success('Saxlandı.');
            setEditModal(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        } finally {
            setSaving(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Layout>
            {/* Responsive kassa table — bütün sütunlar görsənir, font ekrana görə kiçilir */}
            <style>{`
                .kassa-fit-table { table-layout: fixed; width: 100%; }
                .kassa-fit-table th,
                .kassa-fit-table td {
                    font-size: clamp(8px, 0.85vw, 12px) !important;
                    padding: 6px 4px !important;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .kassa-fit-table th { letter-spacing: 0 !important; }
                @media (max-width: 1100px) {
                    .kassa-fit-table th, .kassa-fit-table td {
                        font-size: clamp(7px, 0.75vw, 10px) !important;
                        padding: 5px 3px !important;
                    }
                }
            `}</style>
            <div className="p-3 sm:p-4 max-w-7xl mx-auto">

                {/* Page header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h1 className="text-xl font-bold text-slate-800">💰 Kassa Hesabatı</h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="date"
                            value={fromDate}
                            min={startDate || undefined}
                            max={toDate}
                            onChange={e => setFromDate(e.target.value)}
                            className="border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <span className="text-slate-400 text-xs">—</span>
                        <input
                            type="date"
                            value={toDate}
                            min={startDate || fromDate}
                            onChange={e => setToDate(e.target.value)}
                            className="border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <button
                            onClick={load}
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-1.5 rounded-xl transition-all disabled:opacity-50"
                        >
                            {loading ? '...' : '🔄 Yenilə'}
                        </button>
                        {isAdmin && (
                            <button
                                onClick={async () => {
                                    if (!(await confirm(
                                        startDate
                                            ? `Kassa başlanğıc tarixi indi ${startDate}-dir.\n\nYenidən bugünə (${todayISO()}) təyin etmək istəyirsiniz? Bu tarixdən əvvəlki bütün kassa qeydləri silinəcək.`
                                            : `Kassa başlanğıc tarixini bugünə (${todayISO()}) təyin etmək istəyirsiniz? Daha əvvəlki tarixlər görünməz.`
                                    ))) return;
                                    try {
                                        await api.post('/kassa-ledger/set-start-date', { date: todayISO() });
                                        toast.success('Kassa başlanğıc tarixi təyin edildi.');
                                        load();
                                    } catch (err) {
                                        toast.error(err.response?.data?.msg || 'Xəta.');
                                    }
                                }}
                                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
                                title="Bu tarixdən əvvəlki kassa qeydləri görsənməz"
                            >
                                📅 Bugündən başlat
                            </button>
                        )}
                    </div>
                </div>

                {/* Main layout: left panel + right table */}
                <div className="flex flex-col lg:flex-row gap-4">

                    {/* ── LEFT PANEL: selected day summary ─────────────────── */}
                    <div className="w-full lg:w-72 xl:w-80 shrink-0">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sticky top-4">

                            {/* Day picker inside left panel */}
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Seçilmiş Gün</p>
                                    <p className="text-base font-bold text-slate-800 mt-0.5">
                                        {selected ? fmtDateFull(selected.date) : '—'}
                                    </p>
                                </div>
                                {selected && isAdmin && (
                                    <button
                                        onClick={() => openEdit(selected)}
                                        className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-3 py-1.5 rounded-xl transition-all"
                                    >
                                        ✏️ Düzəlt
                                    </button>
                                )}
                            </div>

                            {loading && !selected ? (
                                <p className="text-xs text-slate-400 text-center py-8">Yüklənir...</p>
                            ) : !selected ? (
                                <p className="text-xs text-slate-400 text-center py-8">Gün seçilməyib.</p>
                            ) : (
                                <div>
                                    {/* Opening */}
                                    <Row label="Əvvələ qalıq" value={selected.opening_balance} color="blue" bold />
                                    <Divider />

                                    {/* Income */}
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2 mb-1">Daxilolma</p>
                                    <Row label="🍽️ Restoran: Nağd" value={selected.restoran_nagd} color="green" sub />
                                    <Row label="💳 Restoran: POS"  value={selected.restoran_pos}  color="muted" sub />
                                    <Row label="🧮 Servis faizi: Nağd" value={selected.ofisiant_faiz}     color="green" sub />
                                    <Row label="🧮 Servis faizi: POS"  value={selected.ofisiant_faiz_pos} color="muted" sub />
                                    <Row label="🏠 Xidmətlər: Nağd" value={selected.xidmet_nagd}  color="green" sub />
                                    <Row label="💳 Xidmətlər: POS"  value={selected.xidmet_pos}   color="muted" sub />
                                    <Row label="📦 Satış: Nağd"     value={selected.satis_nagd}    color="green" sub />
                                    <Row label="💳 Satış: POS"      value={selected.satis_pos}     color="muted" sub />
                                    {showDelivery && (
                                        <Row label="🛵 Food delivery" value={selected.restoran_delivery} color="muted" sub />
                                    )}

                                    {/* POS total summary */}
                                    <div className="mt-1 mb-1 flex items-center justify-between rounded-lg bg-slate-50 px-2 py-1.5">
                                        <span className="text-xs font-bold text-slate-500">💳 POS Cəmi</span>
                                        <span className="text-xs font-bold tabular-nums text-slate-600">
                                            {fmtAZ(n(selected.restoran_pos) + n(selected.xidmet_pos) + n(selected.satis_pos))}
                                        </span>
                                    </div>
                                    <Divider />

                                    {/* Outgoing */}
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2 mb-1">Çıxılma</p>
                                    <Row label="🛒 Bazarlıq"       value={selected.bazarliq_green} color="red" sub />
                                    <Row label="🧹 Digər Bazarlıq" value={selected.bazarliq_red}   color="red" sub />
                                    <Row label="💸 Xərclər"        value={selected.xerc}           color="red" sub />
                                    <Row label="👥 İşçi maaşı"     value={selected.maas}           color="red" sub />
                                    <Row label="🏢 Ofis"           value={selected.ofis_amount}    color="red" sub />

                                    {/* Geri qaytarılma — köhnə (frozen) günə aid refund cari günə düşür */}
                                    {(n(selected.refund_green) > 0 || n(selected.refund_red) > 0) && (
                                        <>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2 mb-1">↩ Geri qaytarılma</p>
                                            {n(selected.refund_green) > 0 && (
                                                <Row label="↩ Geri qaytarılma (+)" value={selected.refund_green} color="green" sub />
                                            )}
                                            {n(selected.refund_red) > 0 && (
                                                <Row label="↩ Geri qaytarılma (−)" value={selected.refund_red} color="red" sub />
                                            )}
                                            {refunds.filter(r => r.day === selected.date).map(r => (
                                                <div key={r.id} className="flex items-center justify-between pl-4 text-[11px] text-slate-400">
                                                    <span>• {r.reason || (r.direction === 'green' ? 'Geri qaytarıldı' : 'Çıxıldı')}</span>
                                                    <span className={`tabular-nums ${r.direction === 'green' ? 'text-emerald-500' : 'text-red-500'}`}>
                                                        {r.direction === 'green' ? '+' : '−'}{fmt2(r.amount)}
                                                    </span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    <Divider />

                                    {/* Seçilmiş tarix — balaca (screenshot üçün) */}
                                    <p className="text-[11px] text-slate-400 text-center mb-1">📅 {fmtDateFull(selected.date)}</p>

                                    {/* Closing */}
                                    <div className={`mt-2 rounded-xl px-3 py-3 ${n(selected.closing_balance) >= 0 ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-slate-700">Sona qalıq</span>
                                            <span className={`text-xl font-bold tabular-nums ${n(selected.closing_balance) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {fmtAZ(selected.closing_balance)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Cəmlər — Nağd (+ servis) və POS (+ servis) ümumi */}
                                    {(() => {
                                        const cashTotal = n(selected.restoran_nagd) + n(selected.ofisiant_faiz) + n(selected.xidmet_nagd) + n(selected.satis_nagd);
                                        const posTotal  = n(selected.restoran_pos)  + n(selected.ofisiant_faiz_pos) + n(selected.xidmet_pos)  + n(selected.satis_pos);
                                        return (
                                            <div className="mt-3 grid grid-cols-2 gap-2">
                                                <div className="rounded-xl px-3 py-3 bg-green-50 border border-green-200">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider text-green-700">💵 Cəmi Nağd</div>
                                                    <div className="text-[10px] text-green-600/70 mt-0.5">+ servis</div>
                                                    <div className="text-2xl font-black tabular-nums text-green-700 mt-1">
                                                        {fmtAZ(cashTotal)}
                                                    </div>
                                                </div>
                                                <div className="rounded-xl px-3 py-3 bg-blue-50 border border-blue-200">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">💳 Cəmi POS</div>
                                                    <div className="text-[10px] text-blue-600/70 mt-0.5">+ servis</div>
                                                    <div className="text-2xl font-black tabular-nums text-blue-700 mt-1">
                                                        {fmtAZ(posTotal)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {showDelivery && (
                                        <div className="mt-2 flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5">
                                            <span className="text-[11px] font-bold uppercase tracking-wider text-orange-700">🛵 Cəmi Food delivery</span>
                                            <span className="text-lg font-black tabular-nums text-orange-700">{fmtAZ(selected.restoran_delivery)}</span>
                                        </div>
                                    )}

                                    {/* POS note */}
                                    <p className="text-[10px] text-slate-400 mt-3 leading-relaxed text-center">
                                        💳 POS məbləğlər kassa balansına <strong>təsir etmir</strong> — yalnız məlumat üçün göstərilir.
                                        {showDelivery && <><br />🛵 Food delivery də kassa balansına təsir etmir (pul platformadan gəlir).</>}
                                    </p>

                                    {/* Notes */}
                                    {selected.notes && (
                                        <div className="mt-3 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                                            <p className="text-xs text-amber-700">📝 {selected.notes}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── RIGHT: days table ─────────────────────────────────── */}
                    <div className="flex-1 min-w-0">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

                            {/* Table header */}
                            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                                <p className="text-sm font-bold text-slate-700">Günlük Kassa</p>
                                <p className="text-xs text-slate-400">{days.length} gün</p>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center py-16">
                                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : days.length === 0 ? (
                                <p className="text-center text-slate-400 text-sm py-16">Bu tarix aralığında məlumat yoxdur.</p>
                            ) : (
                                <div className="w-full">
                                    <table className="w-full text-xs kassa-fit-table">
                                        <thead>
                                            <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                                                <th className="px-3 py-2.5 text-left font-semibold">Tarix</th>
                                                <th className="px-3 py-2.5 text-right font-semibold">Əvvəlki</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-emerald-600">Rest. Nağd</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-slate-400">Rest. POS</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-emerald-600">Xidm. Nağd</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-slate-400">Xidm. POS</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-emerald-600">Satış Nağd</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-slate-400">Satış POS</th>
                                                {showDelivery && <th className="px-3 py-2.5 text-right font-semibold text-orange-500">Delivery</th>}
                                                <th className="px-3 py-2.5 text-right font-semibold text-slate-500 bg-slate-50">POS Cəmi</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-red-500">Bazarlıq</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-red-500">Digər</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-red-500">Xərc</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-red-500">Maaş</th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-red-500">Ofis</th>
                                                <th className="px-3 py-2.5 text-right font-semibold">Sona</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...days].reverse().map((day) => {
                                                const isSelected = day.date === selectedDate;
                                                const closing    = n(day.closing_balance);
                                                return (
                                                    <tr
                                                        key={day.date}
                                                        onClick={() => setSelectedDate(day.date)}
                                                        className={`border-t border-slate-50 cursor-pointer transition-colors ${
                                                            isSelected
                                                                ? 'bg-blue-50'
                                                                : 'hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <td className="px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">
                                                            {fmtDate(day.date)}
                                                            {isSelected && (
                                                                <span className="ml-1 text-blue-500 text-[10px]">◀</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 group relative">
                                                            <span>{fmt2(day.opening_balance)}</span>
                                                            {isAdmin && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); openEdit(day); }}
                                                                    className="ml-1 text-blue-500 hover:text-blue-700 text-[10px] opacity-60 hover:opacity-100"
                                                                    title="Əvvələ qalığı düzəlt"
                                                                >
                                                                    ✎
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 font-medium">
                                                            {fmt2(day.restoran_nagd)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                                                            {fmt2(day.restoran_pos)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 font-medium">
                                                            {fmt2(day.xidmet_nagd)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                                                            {fmt2(day.xidmet_pos)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 font-medium">
                                                            {fmt2(day.satis_nagd)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                                                            {fmt2(day.satis_pos)}
                                                        </td>
                                                        {showDelivery && (
                                                            <td className="px-3 py-2.5 text-right tabular-nums text-orange-500">
                                                                {fmt2(day.restoran_delivery)}
                                                            </td>
                                                        )}
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 font-semibold bg-slate-50">
                                                            {fmt2(n(day.restoran_pos) + n(day.xidmet_pos) + n(day.satis_pos))}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-red-500">
                                                            {fmt2(day.bazarliq_green)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-red-500">
                                                            {fmt2(day.bazarliq_red)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-red-500">
                                                            {fmt2(day.xerc)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-red-500">
                                                            {fmt2(day.maas)}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right tabular-nums text-red-500">
                                                            {fmt2(day.ofis_amount)}
                                                        </td>
                                                        <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${closing >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                            {fmt2(closing)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>

                                        {/* Totals row */}
                                        {days.length > 1 && (() => {
                                            const totRestNagd  = days.reduce((s, d) => s + n(d.restoran_nagd),  0);
                                            const totRestPos   = days.reduce((s, d) => s + n(d.restoran_pos),   0);
                                            const totXidmNagd  = days.reduce((s, d) => s + n(d.xidmet_nagd),   0);
                                            const totXidmPos   = days.reduce((s, d) => s + n(d.xidmet_pos),    0);
                                            const totSatisNagd = days.reduce((s, d) => s + n(d.satis_nagd),    0);
                                            const totSatisPos  = days.reduce((s, d) => s + n(d.satis_pos),     0);
                                            const totDelivery  = days.reduce((s, d) => s + n(d.restoran_delivery), 0);
                                            const totBazGreen  = days.reduce((s, d) => s + n(d.bazarliq_green), 0);
                                            const totBazRed    = days.reduce((s, d) => s + n(d.bazarliq_red),   0);
                                            const totXerc      = days.reduce((s, d) => s + n(d.xerc),          0);
                                            const totMaas      = days.reduce((s, d) => s + n(d.maas),          0);
                                            const totOfis      = days.reduce((s, d) => s + n(d.ofis_amount),   0);
                                            return (
                                                <tfoot>
                                                    <tr className="bg-slate-100 text-slate-600 font-bold border-t-2 border-slate-200 text-[11px]">
                                                        <td className="px-3 py-2.5">Cəmi</td>
                                                        <td className="px-3 py-2.5 text-right">—</td>
                                                        <td className="px-3 py-2.5 text-right text-emerald-600">{fmt2(totRestNagd)}</td>
                                                        <td className="px-3 py-2.5 text-right text-slate-400">{fmt2(totRestPos)}</td>
                                                        <td className="px-3 py-2.5 text-right text-emerald-600">{fmt2(totXidmNagd)}</td>
                                                        <td className="px-3 py-2.5 text-right text-slate-400">{fmt2(totXidmPos)}</td>
                                                        <td className="px-3 py-2.5 text-right text-emerald-600">{fmt2(totSatisNagd)}</td>
                                                        <td className="px-3 py-2.5 text-right text-slate-400">{fmt2(totSatisPos)}</td>
                                                        {showDelivery && <td className="px-3 py-2.5 text-right text-orange-500">{fmt2(totDelivery)}</td>}
                                                        <td className="px-3 py-2.5 text-right text-slate-600 font-bold bg-slate-50">{fmt2(totRestPos + totXidmPos + totSatisPos)}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{fmt2(totBazGreen)}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{fmt2(totBazRed)}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{fmt2(totXerc)}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{fmt2(totMaas)}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{fmt2(totOfis)}</td>
                                                        <td className="px-3 py-2.5 text-right">—</td>
                                                    </tr>
                                                </tfoot>
                                            );
                                        })()}
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Legend */}
                        <div className="mt-3 bg-white rounded-2xl border border-slate-100 shadow-sm p-3">
                            <p className="text-[11px] font-bold text-slate-500 mb-2">Formula:</p>
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                <span className="text-blue-600 font-semibold">Əvvələ qalıq</span>
                                {' + '}
                                <span className="text-emerald-600 font-semibold">Restoran Nağd</span>
                                {' + '}
                                <span className="text-emerald-600 font-semibold">Xidmətlər Nağd</span>
                                {' + '}
                                <span className="text-emerald-600 font-semibold">Satış Nağd</span>
                                {' − '}
                                <span className="text-red-500 font-semibold">Bazarlıq</span>
                                {' − '}
                                <span className="text-red-500 font-semibold">Digər</span>
                                {' − '}
                                <span className="text-red-500 font-semibold">Xərclər</span>
                                {' − '}
                                <span className="text-red-500 font-semibold">Maaş</span>
                                {' − '}
                                <span className="text-red-500 font-semibold">Ofis</span>
                                {' = '}
                                <span className="text-emerald-700 font-bold">Sona qalıq</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Edit modal ──────────────────────────────────────────────── */}
            {editModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
                        <h2 className="text-base font-bold text-slate-800 mb-4">
                            📝 {fmtDateFull(editDate)} — Düzəliş
                        </h2>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">
                                    Əvvələ qalıq (₼)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editOpen}
                                    onChange={e => setEditOpen(e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    placeholder="0.00"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                    Əvvələki gün sonuna qalıq avtomatik gəlir; lazım olsa düzəldin.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">
                                    🏢 Ofis məbləği (₼)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editOfis}
                                    onChange={e => setEditOfis(e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    placeholder="0.00"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                    İcarə, kommunal, ofis xərcləri üçün əl ilə daxil edin.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">
                                    Qeyd (istəyə bağlı)
                                </label>
                                <textarea
                                    value={editNotes}
                                    onChange={e => setEditNotes(e.target.value)}
                                    rows={2}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                                    placeholder="Bu gün üçün qeyd..."
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setEditModal(false)}
                                className="flex-1 border border-slate-200 rounded-xl py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all"
                            >
                                Ləğv et
                            </button>
                            <button
                                onClick={saveEdit}
                                disabled={saving}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                {saving ? 'Saxlanır...' : '💾 Saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
